import { Badge } from "@/components/ui/baseUi/badge"
import { Button } from "@/components/ui/baseUi/button"
import { Checkbox } from "@/components/ui/baseUi/checkbox"
import { errorToastClassName, successToastClassName, appToast as toast } from "@/components/ui/baseUi/sonner"
import { Text } from "@/components/ui/baseUi/text"
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/baseUi/tooltip"
import { useBoxPrograms } from "@/modules/boxProgrammation/hooks/useBoxProgram"
import { CouponType } from "@/modules/common/couponObject/gateway/couponObjectApiDto"
import { formatCurrency } from "@/modules/currency/currency"
import { cn } from "@/modules/lib/utils"
import { useMemberSideBar } from "@/modules/members/hooks/useMemberSidebar"
import { BoxOfferCard } from "@modules/boxOffer/components/boxOfferCard"
import { BoxOfferRuleCard } from "@modules/boxOffer/components/boxOfferRuleCard"
import { BoxOfferInvitationStatus, BoxOfferType } from "@modules/boxOffer/entities/boxOfferTypes"
import { type BoxOfferInvitationInput } from "@modules/boxOffer/gateway/boxOfferDto"
import { useBoxOffer, useBoxOfferInvitations } from "@modules/boxOffer/hooks/useBoxOffer"
import { getOfferCommitment, getOfferVisibility } from "@modules/boxOffer/utils/boxOfferUtils"
import {
  convertCouponInputToRequest,
  type CouponInputFormik,
} from "@modules/boxSubscription/formik/boxSubscriptionFormik"
import {
  getAmountOfDiscount,
  getPriceAfterDiscount,
  getTextDurationType,
} from "@modules/common/couponObject/utils/couponObjectUtils"
import { word } from "@modules/core/utils/i18n"
import { FeatureKeys } from "@modules/featureToggle/featureToggleTypes"
import { useFeatureToggle } from "@modules/featureToggle/hooks/useFeatureToggle"
import { useLanguage } from "@modules/language/hooks/useLanguage"
import { capitalize, shortenText } from "@modules/utils/stringUtils"
import dayjs from "dayjs"
import { CalendarDays, ChevronRight, CircleXIcon, Edit2Icon, LoaderCircle, Percent } from "lucide-react"
import { type Dispatch, type SetStateAction, useMemo } from "react"

export type MemberSendOfferModalStepTwoProps = {
  boxOfferId: string
  userId: string
  memberId?: string
  isSendingToLead?: boolean
  handleStep: () => void
  onHide: () => void
  discountInput: CouponInputFormik
  deferredDate: Date | null
  endDate: Date | null
  setIsDiscountVisible: Dispatch<SetStateAction<boolean>>
  setIsDeferredDateVisible: Dispatch<SetStateAction<boolean>>
  setIsEndDateVisible: Dispatch<SetStateAction<boolean>>
  paymentAtCounter: boolean
  setPaymentAtCounter: Dispatch<SetStateAction<boolean>>
  forceAccept: boolean
  setForceAccept: Dispatch<SetStateAction<boolean>>
  className?: string
} & React.ComponentProps<"div">

export function MemberSendOfferModalStepTwo({
  boxOfferId,
  userId,
  memberId,
  isSendingToLead = false,
  handleStep,
  onHide,
  setIsDiscountVisible,
  setIsDeferredDateVisible,
  setIsEndDateVisible,
  discountInput,
  deferredDate,
  endDate,
  paymentAtCounter,
  setPaymentAtCounter,
  forceAccept,
  setForceAccept,
  className,
  ...props
}: MemberSendOfferModalStepTwoProps) {
  const language = useLanguage()
  const { data: boxOffer } = useBoxOffer(boxOfferId)
  const { data: programs } = useBoxPrograms()
  const isFeatureForceSubscriptionAcceptationEnabled = useFeatureToggle(FeatureKeys.ForceSubscriptionAcceptation)
  const {
    sendBoxOfferInvitation,
    isSendBoxOfferInvitation,
    data: boxOfferInvitations,
  } = useBoxOfferInvitations(userId, memberId)
  const { navigateToMemberDetailsInfo } = useMemberSideBar()

  const offerCommitment = useMemo(() => {
    return getOfferCommitment(boxOffer)
  }, [boxOffer])

  const visibilitySubtitle = useMemo(() => {
    return getOfferVisibility(boxOffer, "var(--textDefault-body)")
  }, [boxOffer])

  const filteredPrograms = useMemo(
    () => programs.filter((program) => boxOffer?.programs.includes(program.id)),
    [boxOffer?.programs, programs],
  )

  const canSetPaymentAtCounter = useMemo(() => {
    return boxOffer?.price?.value && boxOffer.price.value > 0
  }, [boxOffer?.type])

  const canForceSubscriptionAcceptation = useMemo(() => {
    return boxOffer?.type === BoxOfferType.Millenium && isFeatureForceSubscriptionAcceptationEnabled
  }, [boxOffer?.type, isFeatureForceSubscriptionAcceptationEnabled])

  const handleSendOffer = () => {
    if (!boxOffer) return

    if (
      boxOfferInvitations.findIndex(
        (invitation) => invitation.boxOfferId === boxOffer.id && invitation.status === BoxOfferInvitationStatus.PENDING,
      ) >= 0
    ) {
      return toast.error(word("member.sendOffer.modal.toast.duplicateError"), {
        classNames: errorToastClassName(),
      })
    }

    const payload = {
      offerId: boxOffer.id,
      userId,
      coupon: discountInput.value > 0 ? convertCouponInputToRequest(discountInput) : undefined,
      deferredDate: deferredDate ? dayjs(deferredDate).add(1, "day").toISOString().split("T")[0] : undefined,
      subscriptionEndDate: endDate ? dayjs(endDate).add(1, "day").toISOString().split("T")[0] : undefined,
      outOfBand: paymentAtCounter,
      forceAccept,
    } as BoxOfferInvitationInput

    sendBoxOfferInvitation(payload)
      .then(() => {
        toast.success(word("member.sendOffer.modal.toast.success", { name: boxOffer.name }), {
          classNames: successToastClassName(),
        })
        if (isSendingToLead && !!memberId && payload.forceAccept) {
          navigateToMemberDetailsInfo(memberId, {
            replaceAllComponents: true,
          })
        }
        onHide()
      })
      .catch(() => {
        toast.error(word("member.sendOffer.modal.toast.error", { name: boxOffer.name }), {
          classNames: errorToastClassName(),
        })
      })
  }

  return (
    <div className={cn("flex w-full flex-col", className)} {...props}>
      {boxOffer && (
        <div className="mt-6 flex w-full flex-col gap-6">
          {/* HEADER */}
          <BoxOfferCard
            offer={boxOffer}
            key={boxOffer.id}
            discountPrice={
              boxOffer.price !== null && discountInput.value > 0
                ? {
                    value: getPriceAfterDiscount(discountInput, boxOffer.price.value),
                    currency: boxOffer.price.currency,
                  }
                : undefined
            }
            className={`flex w-full justify-between gap-6 rounded-xl border border-borderPrimary bg-neutral-200 p-4`}
          />

          <div className="flex w-full items-center justify-between px-1">
            <Text fontStyle="BM" className="text-textDefault-body">
              {capitalize(offerCommitment)}
              {boxOffer.commitmentPeriodAutoRenewal
                ? `- ${word("box.offer.form.infos.commitmentPeriodAutoRenewal.active")}`
                : ""}
            </Text>
            <Text
              fontStyle="BS"
              className="my-1 flex items-center justify-center gap-1 wrap-break-word whitespace-break-spaces text-textDefault-body"
            >
              {visibilitySubtitle.icon}
              {visibilitySubtitle.text}
            </Text>
          </div>

          {/* NUMBER OF SESSION AND RENEWAL */}
          {((boxOffer.count && boxOffer.count > 0) || boxOffer.renewable) && (
            <div className="flex w-full justify-between gap-4">
              {boxOffer.count && boxOffer.count > 0 && (
                <div className="flex w-6/12 flex-col gap-2">
                  <Text className="text-textDefault-body" fontStyle="LS">
                    {word("offer.form.numberOfSessions.title").toUpperCase()}
                  </Text>
                  <BoxOfferRuleCard className="flex h-full items-center border border-neutral-200 px-8 py-2">
                    <Text className="w-full justify-center text-center" fontStyle="TS">
                      {word("offer.form.numberOfSessions.included", { count: boxOffer.count })}
                    </Text>
                  </BoxOfferRuleCard>
                </div>
              )}
              {boxOffer.renewable && (
                <div className="flex w-6/12 flex-col gap-2">
                  <Text className="text-textDefault-body" fontStyle="LS">
                    {word("box.offer.form.infos.renewal.title").toUpperCase()}
                  </Text>
                  <BoxOfferRuleCard className="flex h-full items-center border border-neutral-200 px-8 py-2">
                    <Text className="w-full text-center" fontStyle="TS">
                      {word("box.offer.form.infos.renewal.yes")}
                    </Text>
                  </BoxOfferRuleCard>
                </div>
              )}
            </div>
          )}

          {/* RULES FOR RYTHME */}
          {boxOffer.type !== BoxOfferType.DropIn && boxOffer.type !== BoxOfferType.Trial && (
            <div className="flex flex-col gap-2">
              <Text className="text-textDefault-body" fontStyle="LS">
                {word("box.offer.form.infos.restrictedPeriod.title").toUpperCase()}
              </Text>
              <div className="flex w-full gap-2">
                {boxOffer.rules.length > 0 ? (
                  boxOffer.rules.map((rule, index) => (
                    <BoxOfferRuleCard
                      key={index}
                      className="flex h-full items-center border border-neutral-200 px-8 py-2"
                    >
                      <Text className="w-full text-center" fontStyle="TS">{`${rule.value.toString()} ${word(
                        "offer.form.rules.sessions",
                        { count: rule.value },
                      )} ${word(`offer.form.rules.period.per.${rule.period}`)}`}</Text>
                    </BoxOfferRuleCard>
                  ))
                ) : (
                  <BoxOfferRuleCard className="border-neutralColor-300 rounded-card flex w-full items-center border border-solid px-8 py-2">
                    <Text className="w-full text-center" fontStyle="TS">
                      {word("box.offer.form.infos.rules.unlimited")}
                    </Text>
                  </BoxOfferRuleCard>
                )}
              </div>
            </div>
          )}

          {/* ACTIVITIES */}
          <div className="flex flex-col gap-2">
            <Text className="text-textDefault-body" fontStyle="LS">
              {word("box.offer.form.infos.activitiesIncluded.title").toUpperCase()}
            </Text>
            <div className="flex min-h-25 flex-col rounded-3xl border border-neutral-300 px-8 py-6">
              <Text fontStyle="BS">{word("box.offer.resume.activitiesIncluded.details")}</Text>
              <div className="mt-2 flex flex-wrap gap-2">
                {filteredPrograms.map((program, index) =>
                  program.name.length > 65 ? (
                    <>
                      <Tooltip key={"tooltip-program-" + index}>
                        <TooltipTrigger
                          render={
                            <Badge
                              key={"badge-program-" + index}
                              variant="neutralIntense"
                              size="lg"
                              className="wrap-break-word whitespace-break-spaces"
                            >
                              {shortenText(program.name, 28)}
                            </Badge>
                          }
                        />
                        <TooltipContent side="bottom" className="w-fit max-w-60 wrap-anywhere">
                          {program.name}
                        </TooltipContent>
                      </Tooltip>
                    </>
                  ) : (
                    <Badge key={"badge-program-" + index.toString()} variant="neutralIntense" size="lg">
                      {program.name}
                    </Badge>
                  ),
                )}
              </div>
            </div>
          </div>

          {/* DISCOUNT & START DATE & SUBSCRIPTION END DATE */}
          <div className="flex flex-col">
            {boxOffer.price && (
              <div
                className="flex cursor-pointer items-center gap-2 border-t border-neutral-200 p-2 hover:bg-neutral-100"
                onClick={() => {
                  setIsDiscountVisible(true)
                }}
              >
                <Percent color="currentColor" className="text-textDefault-body" strokeWidth={1} size={22} />
                <Text fontStyle="BM" className="flex grow text-textDefault-body">
                  {discountInput.value
                    ? `${word("box.offer.send.modal.button.editDiscount")}: ${
                        discountInput.type === CouponType.PERCENT
                          ? `-${discountInput.value.toString()}%`
                          : `-${formatCurrency(
                              getAmountOfDiscount(discountInput, boxOffer.price.value),
                              boxOffer.price.currency,
                              language,
                            )}`
                      } ${boxOffer.type === BoxOfferType.Subscription ? `(${getTextDurationType(discountInput)})` : ""}`
                    : word("box.offer.send.modal.button.addDiscount")}
                </Text>

                {discountInput.value ? (
                  <Edit2Icon className="flex size-5 text-textDefault-body" color="currentColor" strokeWidth={1} />
                ) : (
                  <ChevronRight className="flex text-textDefault-body" color="currentColor" strokeWidth={1} />
                )}
              </div>
            )}

            {boxOffer.type === BoxOfferType.Subscription && (
              <div
                className="flex cursor-pointer items-center gap-2 border-t border-b border-neutral-200 p-2 hover:bg-neutral-100"
                onClick={() => {
                  setIsDeferredDateVisible(true)
                }}
              >
                <CalendarDays color="currentColor" className="text-textDefault-body" strokeWidth={1} size={22} />
                <Text fontStyle="BM" className="flex grow text-textDefault-body">
                  {deferredDate
                    ? `${word("box.offer.send.modal.button.editStartDate")}: ${dayjs(deferredDate).format("ll")}`
                    : word("box.offer.send.modal.button.setStartDate")}
                </Text>
                {deferredDate ? (
                  <Edit2Icon className="flex size-5 text-textDefault-body" color="currentColor" strokeWidth={1} />
                ) : (
                  <ChevronRight className="flex text-textDefault-body" color="currentColor" strokeWidth={1} />
                )}
              </div>
            )}

            {boxOffer.type === BoxOfferType.Subscription && (
              <div
                className="flex cursor-pointer items-center gap-2 border-t border-b border-neutral-200 p-2 hover:bg-neutral-100"
                onClick={() => {
                  setIsEndDateVisible(true)
                }}
              >
                <CircleXIcon color="currentColor" className="text-textDefault-body" strokeWidth={1} size={22} />
                <Text fontStyle="BM" className="flex grow text-textDefault-body">
                  {endDate
                    ? `${word("box.offer.send.modal.button.editEndDate")}: ${dayjs(endDate).format("ll")}`
                    : word("box.offer.send.modal.button.setEndDate")}
                </Text>
                {endDate ? (
                  <Edit2Icon className="flex size-5 text-textDefault-body" color="currentColor" strokeWidth={1} />
                ) : (
                  <ChevronRight className="flex text-textDefault-body" color="currentColor" strokeWidth={1} />
                )}
              </div>
            )}
          </div>

          {canForceSubscriptionAcceptation && (
            <div className="flex items-start gap-4 pt-2">
              <Checkbox
                checked={forceAccept}
                onCheckedChange={() => {
                  setForceAccept(!forceAccept)
                }}
                name="forceAccept"
              />
              <div className="flex grow flex-col gap-2">
                <Text fontStyle="BM" className="text-textDefault-body">
                  {word("box.offer.send.modal.forceAccept")}
                </Text>
                <Text fontStyle="BS" className="text-textDefault-body">
                  {word("box.offer.send.modal.forceAcceptDescription")}
                </Text>
              </div>
            </div>
          )}

          {canSetPaymentAtCounter && (
            <div className="flex items-start gap-4 pt-2">
              <Checkbox
                size="lg"
                checked={paymentAtCounter}
                onCheckedChange={() => {
                  setPaymentAtCounter(!paymentAtCounter)
                }}
                name="paymentAtCounter"
                className="mt-1"
              />

              <div className="flex grow flex-col gap-2">
                <Text fontStyle="BM" className="text-textDefault-body">
                  {word("box.offer.send.modal.paymentAtCounter")}
                </Text>
                <Text fontStyle="BS" className="text-textDefault-body">
                  {word("box.offer.send.modal.paymentAtCounterDescription")}
                </Text>
              </div>
            </div>
          )}

          {/* BUTTON */}
          <div className="mt-4 mb-6 flex w-full flex-row items-center justify-center gap-2">
            <Button variant="outline" size="lg" onClick={handleStep} className="grow">
              {word("global.return")}
            </Button>

            <Button
              variant="primary"
              size="lg"
              onClick={handleSendOffer}
              className="grow"
              disabled={isSendBoxOfferInvitation}
              isLoading={isSendBoxOfferInvitation}
            >
              {isSendBoxOfferInvitation && <LoaderCircle className="animate-spin" />}
              {word("box.offer.send.modal.button.send")}
            </Button>
          </div>
        </div>
      )}
    </div>
  )
}
